/**
 * Road identity: "which road am I on, which road am I turning onto".
 *
 * The driver asked for road NUMBERS on every sign, and for good reason - on
 * Thai highways the number is what's painted on the overhead gantry, while the
 * OSM name is a Thai string an English-speaking driver can't parse at 100 km/h.
 *
 * Sources, in order of trust:
 *  1. `step.ref` - the authoritative route number ("304", "304; AH19").
 *  2. Banner components of type `icon` / `exit-number` - what Mapbox's own UI
 *     renders as a shield. These carry the number even when `ref` is absent
 *     (a fork onto 304 has no ref until the following step).
 *  3. `step.destinations` - "Nakhon Ratchasima, 304".
 *  4. The road name, but only when the active language can read it.
 */

import type { BannerContent, BannerInstruction, RouteStep } from '../core/types';
import { keepReadable, readableIn } from '../core/lang';
import type { Lang } from '../core/i18n';

/** A route number looks like 304, AH19, E22, 3701 - digits, optionally prefixed. */
const REF_SHAPE = /^[A-Z]{0,3}-?\d{1,4}[A-Z]?$/;

function splitRefs(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[;,/]/)
    .map((s) => s.trim())
    .filter((s) => REF_SHAPE.test(s));
}

/** Route numbers carried by a step, most significant first. e.g. ['304','AH19'] */
export function stepRefs(step: RouteStep | null | undefined): string[] {
  if (!step) return [];
  const out = splitRefs(step.ref);
  if (out.length) return out;

  // Fall back to the shield icons in the step's own banner.
  for (const b of step.bannerInstructions ?? []) {
    const fromBanner = bannerRefs(b.primary).concat(bannerRefs(b.secondary));
    if (fromBanner.length) return fromBanner;
  }
  return splitRefs(step.destinations);
}

/** Route numbers a banner renders as shields. */
export function bannerRefs(content: BannerContent | undefined): string[] {
  if (!content?.components?.length) return [];
  const out: string[] = [];
  for (const c of content.components) {
    if (c.type !== 'icon' && c.type !== 'exit-number' && c.type !== 'exit') continue;
    const text = (c.text ?? '').trim();
    if (REF_SHAPE.test(text)) out.push(text);
  }
  if (out.length) return out;
  // Some responses put the number in a plain text component with no icon type.
  return splitRefs(content.text);
}

/** The single road number to badge, or null when the road is unnumbered. */
export function primaryRef(step: RouteStep | null | undefined): string | null {
  return stepRefs(step)[0] ?? null;
}

/**
 * Human label for the road a step travels along, safe for `lang`.
 * Prefers the number; uses the name only when it's readable in this language.
 */
export function roadLabel(step: RouteStep | null | undefined, lang: Lang): string {
  if (!step) return '';
  const refs = stepRefs(step);
  if (refs.length) return refs.slice(0, 2).join(' / ');
  const name = (step.name ?? '').trim();
  if (name && readableIn(name, lang)) return name;
  return '';
}

/**
 * Banner text with unreadable scripts stripped, falling back to the road
 * number. This is what stops "ถนนมหาจักรพรรดิ์" appearing on an English sign.
 */
export function safeBannerText(
  banner: BannerInstruction | null,
  step: RouteStep | null,
  lang: Lang,
): string {
  const raw = banner?.primary.text ?? '';
  const cleaned = keepReadable(raw, lang);
  if (cleaned && !REF_SHAPE.test(cleaned)) return cleaned;

  // Nothing readable survived (or all that survived was the number itself):
  // prefer the shield of the road we're joining.
  const refs = bannerRefs(banner?.primary).concat(stepRefs(step));
  if (refs.length) return refs[0];
  return cleaned;
}

/** Language-safe descriptions of Mapbox's road classes. */
const CLASS_LABELS: Record<string, { en: string; th: string }> = {
  motorway: { en: 'Motorway', th: 'มอเตอร์เวย์' },
  motorway_link: { en: 'Slip road', th: 'ทางลาด' },
  trunk: { en: 'Highway', th: 'ทางหลวง' },
  trunk_link: { en: 'Slip road', th: 'ทางลาด' },
  primary: { en: 'Main road', th: 'ถนนสายหลัก' },
  primary_link: { en: 'Slip road', th: 'ทางลาด' },
  secondary: { en: 'Secondary road', th: 'ถนนสายรอง' },
  tertiary: { en: 'Local road', th: 'ถนนท้องถิ่น' },
  street: { en: 'Street', th: 'ถนน' },
  street_limited: { en: 'Street', th: 'ถนน' },
  service: { en: 'Service road', th: 'ทางบริการ' },
  track: { en: 'Track', th: 'ทางลูกรัง' },
  ferry: { en: 'Ferry', th: 'เรือข้ามฟาก' },
};

/**
 * Label for the permanent road strip on the sign.
 *
 * Same rules as `roadLabel`, but when a road has neither a route number nor a
 * name the driver can read, it falls back to describing the road class
 * ("Main road") rather than leaving the strip blank. Most Bangkok streets have
 * Thai-only names and no number, and an empty strip told the driver nothing.
 * Announcements deliberately do NOT use this - "turn right onto main road" is
 * noise, whereas "turn right" is clear.
 */
export function roadStripLabel(step: RouteStep | null | undefined, lang: Lang): string {
  const direct = roadLabel(step, lang);
  if (direct) return direct;
  if (!step) return '';

  for (const x of step.intersections ?? []) {
    const cls = x.mapbox_streets_v8?.class;
    const entry = cls ? CLASS_LABELS[cls] : undefined;
    if (entry) return entry[lang];
  }
  return '';
}

/** True when the two steps run along different numbered roads. */
export function refChanges(from: RouteStep | null, to: RouteStep | null): boolean {
  const a = stepRefs(from);
  const b = stepRefs(to);
  if (!a.length && !b.length) return false;
  if (!a.length || !b.length) return true;
  // Sharing any number means we're still on the same road (304 -> 304/AH19).
  return !a.some((r) => b.includes(r));
}

/** True when the road NAME changes and both names are meaningful. */
export function nameChanges(from: RouteStep | null, to: RouteStep | null): boolean {
  const a = (from?.name ?? '').trim();
  const b = (to?.name ?? '').trim();
  if (!a || !b) return false;
  return a !== b;
}
